import { useEffect, useLayoutEffect, useRef, useState } from 'react';

/** Slightly slower ticks so the bar eases toward the cap instead of racing there. */
const TICK_MS = 1200;
/** Asymptotic cap while waiting for network (never quite “done” until fetch resolves). */
const ASYMPTOTE_CAP = 93;
/** Lower = slower climb (less aggressive curve toward the cap). */
const EXP_COEFFICIENT = 0.2;

/**
 * Indeterminate-style progress that advances on a diminishing curve for perceived progress,
 * then snaps to 100% when `isLoading` becomes false.
 */
export function useSurveyGroupCompareLoadingProgress(isLoading: boolean): {
  visible: boolean;
  percent: number;
} {
  const [visible, setVisible] = useState(false);
  const [percent, setPercent] = useState(0);
  const wasLoadingRef = useRef(false);

  /** Run before paint so the bar appears on the first frame while loading (no flash of empty state). */
  useLayoutEffect(() => {
    if (isLoading) {
      wasLoadingRef.current = true;
      setVisible(true);
      setPercent(0);
      let tick = 0;
      const id = window.setInterval(() => {
        tick += 1;
        setPercent((prev) => {
          const asymptotic = ASYMPTOTE_CAP * (1 - Math.exp(-EXP_COEFFICIENT * tick));
          return Math.min(ASYMPTOTE_CAP, Math.max(prev, asymptotic));
        });
      }, TICK_MS);
      return () => {
        window.clearInterval(id);
      };
    }

    return undefined;
  }, [isLoading]);

  useEffect(() => {
    if (isLoading) {
      return undefined;
    }

    if (wasLoadingRef.current) {
      wasLoadingRef.current = false;
      setPercent(100);
      const t = window.setTimeout(() => {
        setVisible(false);
        setPercent(0);
      }, 480);
      return () => {
        window.clearTimeout(t);
      };
    }

    return undefined;
  }, [isLoading]);

  return { visible, percent };
}
